import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { ColumnDef, createColumnHelper, ExpandedState, Row, SortingState } from '@tanstack/react-table';

import {
  buildEntityCell,
  subRowStyle,
  TableWrapper,
} from 'components/insights/careTransitions/shared/slideout/expandableRowStyles';
import { attachSubRows } from 'components/insights/careTransitions/shared/slideout/rollupHelpers';
import { formatDaysValue, formatPercentage, formatWholeNumber } from 'components/insights/formatting';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import DataTable from 'components/shared/DataTable';
import SegmentedControl from 'components/shared/SegmentedControl';
import { useBusinessDevelopmentActions, useBusinessDevelopmentStore } from 'stores/businessDevelopmentStore';

import BusinessDevelopmentApiExport from '../BusinessDevelopmentApiExport';
import {
  buildSlideoutExportParams,
  entityFilterForViewBy,
  getDefaultSlideoutDimension,
  pivotSlug,
  SlideoutViewByOption,
  TABLE_PIVOT,
  TablePivot,
  ViewBy,
  viewBySlug,
} from '../constants';
import { buildDischargesRows, DISCHARGES_DEFAULT_SORT, DischargesRow } from '../table/dischargesColumns';
import { buildPatientVolumeRows, PATIENT_VOLUME_DEFAULT_SORT, PatientVolumeRow } from '../table/patientVolumeColumns';
import { buildReferralsRows, REFERRALS_DEFAULT_SORT, ReferralsRow } from '../table/referralsColumns';
import {
  CELL_PADDING,
  CELL_PADDING_FIXED,
  CELL_PADDING_LEFT,
  CELL_PADDING_LEFT_FIXED,
  GreenText,
  RedText,
  STRETCH_CELL,
} from '../table/shared';

// Build rows directly from grouping:0 response data for single-dimension queries.
// AnalyticsResponseRollupCollection filters out grouping:0 rows, so we bypass it.
export function buildFlatRows(
  rollups: AnalyticsResponseRollupCollection
): { entityName: string; metricsByName: Map<string, number | null> }[] {
  const { analyticsResponse } = rollups;
  const metricHeaders = analyticsResponse.metricHeaders.map((h) => h.name);
  return analyticsResponse.responseData
    .filter((row) => (row.grouping ?? 0) === 0 && (row.datesIndex ?? 0) === 0)
    .map((row) => {
      // Intentionally not using renameNoneEntity here — the landing page shows "Other" for
      // payer source nulls, but the slideout breakdown should show "None" for all view bys
      // (e.g., clicking "Other" then viewing by Plan Type should show the actual "None" entity).
      const name = row.dimensionValues[0]?.value != null ? String(row.dimensionValues[0].value) : 'None';
      const metricsByName = new Map<string, number | null>();
      metricHeaders.forEach((h, i) => {
        metricsByName.set(h, row.metricValues[i]?.value != null ? Number(row.metricValues[i].value) : null);
      });
      return { entityName: name, metricsByName };
    })
    .filter((row) => row.entityName);
}

// --- Row type with sub-row support ---

type BreakdownRow = (PatientVolumeRow | DischargesRow | ReferralsRow) & {
  isSubRow?: boolean;
  subRows?: BreakdownRow[];
};

function getSubRows(row: BreakdownRow): BreakdownRow[] | undefined {
  return row.subRows;
}

// --- Slideout-specific column builders (entity names use expandable cell pattern) ---

function buildSlideoutPatientVolumeColumns(
  entityHeader: string,
  groupTypeName: string
): ColumnDef<BreakdownRow, any>[] {
  const helper = createColumnHelper<BreakdownRow>();
  return [
    helper.accessor('entityName', {
      header: entityHeader,
      cell: buildEntityCell,
      size: 320,
      enableSorting: true,
      ...CELL_PADDING_LEFT_FIXED,
    }),
    helper.accessor('volume' as any, {
      header: `${groupTypeName} Volume`,
      cell: (info) => formatWholeNumber(info.getValue()),
      size: 150,
      enableSorting: true,
      ...CELL_PADDING_LEFT,
    }),
    helper.accessor('avgLos' as any, {
      header: 'Avg. Length of Service',
      cell: (info) => formatDaysValue(info.getValue()),
      size: 150,
      enableSorting: true,
      ...CELL_PADDING,
    }),
  ];
}

function buildSlideoutDischargesColumns(entityHeader: string): ColumnDef<BreakdownRow, any>[] {
  const helper = createColumnHelper<BreakdownRow>();
  return [
    helper.accessor('entityName', {
      header: entityHeader,
      cell: buildEntityCell,
      size: 320,
      enableSorting: true,
      ...CELL_PADDING_LEFT_FIXED,
    }),
    helper.accessor('discharges' as any, {
      header: 'Total Discharges',
      cell: (info) => formatWholeNumber(info.getValue()),
      size: 150,
      enableSorting: true,
      ...CELL_PADDING_LEFT,
    }),
    helper.accessor('readmissionRate' as any, {
      header: 'Readmission Rate (%)',
      cell: (info) => formatPercentage(info.getValue()),
      size: 150,
      enableSorting: true,
      ...CELL_PADDING,
    }),
  ];
}

function buildSlideoutReferralsColumns(entityHeader: string): ColumnDef<BreakdownRow, any>[] {
  const helper = createColumnHelper<BreakdownRow>();
  return [
    helper.accessor('entityName', {
      header: entityHeader,
      cell: buildEntityCell,
      size: 320,
      enableSorting: true,
      ...CELL_PADDING_LEFT_FIXED,
    }),
    helper.accessor('totalReferrals' as any, {
      header: 'Total Referrals',
      cell: (info) => formatWholeNumber(info.getValue()),
      size: 140,
      enableSorting: true,
      ...CELL_PADDING_LEFT,
    }),
    helper.accessor('acceptedRate' as any, {
      header: 'Acceptance Rate (%)',
      cell: (info) => <GreenText>{formatPercentage(info.getValue())}</GreenText>,
      size: 165,
      enableSorting: true,
      ...STRETCH_CELL,
    }),
    helper.accessor('denialRate' as any, {
      header: 'Denial Rate (%)',
      cell: (info) => <RedText>{formatPercentage(info.getValue())}</RedText>,
      size: 135,
      enableSorting: true,
      ...STRETCH_CELL,
    }),
    helper.accessor('createdToDischargeDays' as any, {
      header: 'Avg. Created to Discharge',
      cell: (info) => formatDaysValue(info.getValue()),
      size: 185,
      enableSorting: true,
      ...CELL_PADDING_FIXED,
    }),
    helper.accessor('acceptedToDischargeDays' as any, {
      header: 'Avg. Accepted to Discharge',
      cell: (info) => formatDaysValue(info.getValue()),
      size: 205,
      enableSorting: true,
      ...CELL_PADDING_FIXED,
    }),
  ];
}

// --- Table config ---

type TableConfig = {
  columns: ColumnDef<BreakdownRow, any>[];
  data: BreakdownRow[];
  defaultSort: SortingState;
};

type Props = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
  viewBy: ViewBy;
  entityId: string;
  entityName: string;
  tablePivot: TablePivot;
  activeOption: SlideoutViewByOption;
  viewByOptions: SlideoutViewByOption[];
};

export default function SlideoutBreakdownTable({
  loading,
  rollups,
  viewBy,
  entityId,
  entityName,
  tablePivot,
  activeOption,
  viewByOptions,
}: Props) {
  const slideoutDimension = useBusinessDevelopmentStore((s) => s.slideoutDimension);
  const groupTypeName = useBusinessDevelopmentStore((s) => s.selectedGroupType!.displayName);
  const { setSlideoutDimension } = useBusinessDevelopmentActions();

  const activeDimension = slideoutDimension ?? getDefaultSlideoutDimension(viewBy);

  const [expanded, setExpanded] = useState<ExpandedState>({});

  const entityHeader = activeOption.label;

  const tableConfig = useMemo<TableConfig>(() => {
    const withSubs = (
      rows: any[],
      buildSubRow: (name: string, metricValues: (number | null)[], metricHeaders: string[]) => any
    ): BreakdownRow[] =>
      activeOption.hasSubRows ? (attachSubRows(rows, rollups, buildSubRow) as unknown as BreakdownRow[]) : rows;

    const flatRows = activeOption.hasSubRows ? [] : buildFlatRows(rollups);

    const m = (metrics: Map<string, number | null>, key: string) => metrics.get(key) ?? null;

    switch (tablePivot) {
      case TABLE_PIVOT.DISCHARGES:
        return {
          columns: buildSlideoutDischargesColumns(entityHeader),
          data: activeOption.hasSubRows
            ? withSubs(buildDischargesRows(rollups), (name, mv, mh) => ({
                entityName: name,
                entityId: '',
                isSubRow: true,
                discharges: mv[mh.indexOf('id_count')] ?? 0,
                readmissionRate: mv[mh.indexOf('readmission_rate')] ?? null,
              }))
            : (flatRows.map((row) => ({
                entityName: row.entityName,
                entityId: '',
                discharges: m(row.metricsByName, 'id_count') ?? 0,
                readmissionRate: m(row.metricsByName, 'readmission_rate'),
              })) as BreakdownRow[]),
          defaultSort: [DISCHARGES_DEFAULT_SORT],
        };
      case TABLE_PIVOT.REFERRALS:
        return {
          columns: buildSlideoutReferralsColumns(entityHeader),
          data: activeOption.hasSubRows
            ? withSubs(buildReferralsRows(rollups), (name, mv, mh) => ({
                entityName: name,
                entityId: '',
                isSubRow: true,
                totalReferrals: mv[mh.indexOf('review_id_count')] ?? 0,
                acceptedRate: mv[mh.indexOf('acceptance_rate')] ?? null,
                denialRate: mv[mh.indexOf('denial_rate')] ?? null,
                createdToDischargeDays: mv[mh.indexOf('created_to_discharge_days_average')] ?? null,
                acceptedToDischargeDays: mv[mh.indexOf('accepted_to_discharge_days_average')] ?? null,
              }))
            : (flatRows.map((row) => ({
                entityName: row.entityName,
                entityId: '',
                totalReferrals: m(row.metricsByName, 'review_id_count') ?? 0,
                acceptedRate: m(row.metricsByName, 'acceptance_rate'),
                denialRate: m(row.metricsByName, 'denial_rate'),
                createdToDischargeDays: m(row.metricsByName, 'created_to_discharge_days_average'),
                acceptedToDischargeDays: m(row.metricsByName, 'accepted_to_discharge_days_average'),
              })) as BreakdownRow[]),
          defaultSort: [REFERRALS_DEFAULT_SORT],
        };
      default:
        return {
          columns: buildSlideoutPatientVolumeColumns(entityHeader, groupTypeName),
          data: activeOption.hasSubRows
            ? withSubs(buildPatientVolumeRows(rollups), (name, mv, mh) => ({
                entityName: name,
                entityId: '',
                isSubRow: true,
                volume: mv[mh.indexOf('location_episode_id_count')] ?? 0,
                avgLos: mv[mh.indexOf('length_of_stay_average')] ?? null,
              }))
            : (flatRows.map((row) => ({
                entityName: row.entityName,
                entityId: '',
                volume: m(row.metricsByName, 'location_episode_id_count') ?? 0,
                avgLos: m(row.metricsByName, 'length_of_stay_average'),
              })) as BreakdownRow[]),
          defaultSort: [PATIENT_VOLUME_DEFAULT_SORT],
        };
    }
  }, [tablePivot, entityHeader, groupTypeName, rollups, activeOption.hasSubRows]);

  const extraFilters = useMemo(
    () => entityFilterForViewBy(viewBy, entityId, entityName),
    [viewBy, entityId, entityName]
  );

  const exportParams = useMemo(
    () => buildSlideoutExportParams(tablePivot, activeOption),
    [tablePivot, activeOption.value] // eslint-disable-line react-hooks/exhaustive-deps -- primitive dep avoids re-render on object recreation
  );

  const { hasSubRows } = activeOption;
  const rowStyle = useMemo(() => (hasSubRows ? (row: Row<BreakdownRow>) => subRowStyle(row) : undefined), [hasSubRows]);

  const entitySlug = entityName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const slideoutViewBy = activeOption.label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const exportFilename = `${viewBySlug(viewBy)}-${entitySlug}-${pivotSlug(tablePivot)}-by-${slideoutViewBy}`;

  return (
    <Wrapper>
      <ViewByRow>
        <ViewByLabel>View By:</ViewByLabel>
        <SegmentedControl
          options={viewByOptions.map((o) => ({ label: o.label, value: o.value }))}
          selectedValue={activeDimension}
          onChange={(val) => {
            setSlideoutDimension(val);
            setExpanded({});
          }}
        />
        <ExportSlot>
          <BusinessDevelopmentApiExport params={exportParams} extraFilters={extraFilters} filename={exportFilename} />
        </ExportSlot>
      </ViewByRow>
      <TableWrapper>
        <DataTable
          key={`${tablePivot}:${activeDimension}`}
          columns={tableConfig.columns}
          data={tableConfig.data}
          loading={loading}
          freezeFirstColumn
          hasFooter={false}
          defaultSortBy={tableConfig.defaultSort}
          getSubRows={hasSubRows ? getSubRows : undefined}
          expanded={hasSubRows ? expanded : undefined}
          onExpandedChange={hasSubRows ? setExpanded : undefined}
          rowStyle={rowStyle}
        />
      </TableWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ViewByRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 800px) {
    flex-wrap: wrap;
  }
`;

const ExportSlot = styled.div`
  margin-left: auto;
  align-self: flex-start;
  display: flex;
  align-items: center;
`;

const ViewByLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black75};
  letter-spacing: 0.18px;

  @media (max-width: 800px) {
    display: none;
  }
`;
